/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	IEventDefinitionMap,
	ISchemaActionRefModel,
	ISchemaComponentModel,
	ISchemaConstObject,
	ISchemaDataSourceModel,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinition,
	ISchemaFlowNodeTypeDefinitionMap,
	TGenericBlueprintSchema,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { CoreComponentEventNodeTypes } from "@adapptio/lib-core";
import { emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid_v4 } from "uuid";
import { ReactFlowInstance } from "@adapptio/vendor-react-flow-renderer";

import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { FC_ANCHOR } from "../../../../ui/components/FloatingContainer";
import {
	FLOW_EDITOR_DND_MIMETYPE,
	FlowEditor,
	IFlowEditor
} from "../../../../ui/components/FlowEditor/FlowEditor";
import { FLOW_EDITOR_LAYOUT, TGetNodeInfoFn } from "../../../../ui/components/FlowEditor/FlowEditorTypes";
import { Icon, ICON_SIZE } from "../../../../ui/components/Icon";
import { Panel } from "../../../../ui/components/Panel";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ToolbarNote } from "../../../../ui/components/Toolbar/ToolbarNote";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { Tooltip } from "../../../../ui/components/Tooltip";
import { VIEW_SELECTION_ITEM_TYPE, ViewEditSelection } from "../../Common/ViewEditSelection";

import { ViewModel } from "../../Common/ViewModel";
import { ViewEditorView } from "../ViewEditorView";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";

enum EVENT_FLOW_OWNER_KIND {
	COMPONENT = "component",
	DATASOURCE = "dataSource",
	VIEW = "view"
}

interface EventFlowSelection_Base {
	eventName: string;
	flowNodeListModel: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>;
}

interface EventFlowSelection_Component extends EventFlowSelection_Base {
	kind: EVENT_FLOW_OWNER_KIND.COMPONENT;
	componentModel: ISchemaComponentModel;
}

interface EventFlowSelection_DataSource extends EventFlowSelection_Base {
	kind: EVENT_FLOW_OWNER_KIND.DATASOURCE;
	dataSourceModel: ISchemaDataSourceModel;
}

interface EventFlowSelection_View extends EventFlowSelection_Base {
	kind: EVENT_FLOW_OWNER_KIND.VIEW;
	eventSchema: ISchemaConstObject<TSchemaConstObjectProps>;
}

type TEventFlowSelection =
	EventFlowSelection_Component |
	EventFlowSelection_DataSource |
	EventFlowSelection_View;

export interface IViewEditorEventFlow_PaletteItemProps {
	typeName: string;
	nodeType: ISchemaFlowNodeTypeDefinition<TGenericBlueprintSchema>;
}

export interface IViewEditorEventFlow_PaletteProps {}

export interface IViewEditorEventFlowToolbarProps {
	view: ViewEditorView;
}

export interface IViewEditorEventFlowContentProps {
	view: ViewEditorView;
}

interface IAvailableEvent {
	name: string;
	label: string;
	icon?: string;
}

function getAvailableEvents(
	model: ViewModel,
	selection: ViewEditSelection,
	t: (domain: string, term: string) => string
): IAvailableEvent[] {
	const selectedItems = selection.getItems();

	if (selectedItems.length > 1) {
		return [];
	}

	if (selectedItems.length === 0) {
		// View-level events
		const viewEvents = model.getModel().props.spec.value.props.events.value.props;
		return Object.keys(viewEvents).map((key) => ({
			name: key,
			label: t("editor", viewEvents[key]?.schema?.opts?.label || key),
			icon: "mdi/lightning-bolt"
		}));
	}

	const selectedItem = selectedItems[0];
	const selectedItemModel = model.getDesignContext().getNodeById(selectedItem.nodeId);

	if (!selectedItemModel) {
		return [];
	}

	if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.COMPONENT) {
		const cmpModel = selectedItemModel as ISchemaComponentModel;
		const eventDefs = cmpModel.componentHandler.events as IEventDefinitionMap;

		return Object.keys(eventDefs).map((key) => ({
			name: key,
			label: t("editor", eventDefs[key].label),
			icon: eventDefs[key].icon
		}));
	}

	if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.DATASOURCE) {
		const dsModel = selectedItemModel as ISchemaDataSourceModel;
		const eventDefs = dsModel.dsHandler.events as IEventDefinitionMap;

		return Object.keys(eventDefs).map((key) => ({
			name: key,
			label: t("editor", eventDefs[key].label),
			icon: eventDefs[key].icon
		}));
	}

	return [];
}

function getEventFlowSelection(model: ViewModel, selection: ViewEditSelection): TEventFlowSelection {
	if (!selection.isEventSelected()) {
		return null;
	}

	const selectedItems = selection.getItems();
	const eventName = selection.getEventName();

	if (selectedItems.length > 1) {
		// Multiselect - not supported
		return null;

	} else if (selectedItems.length === 0) {
		// Event is directly on view (no model selected)
		const _flowNodeListModel =
			model.getModel().props.spec.value.props.events.value.props[eventName]?.props.nodes.template;

		if (!_flowNodeListModel) {
			return null;
		}

		return {
			kind: EVENT_FLOW_OWNER_KIND.VIEW,
			eventName: eventName,
			eventSchema: model.getModel().props.spec.value.props.events.value.props[eventName]?.schema,
			flowNodeListModel: _flowNodeListModel
		};
	} else {
		// Normal event
		const selectedItem = selectedItems[0];
		const selectedItemModel = model.getDesignContext().getNodeById(selectedItem.nodeId);

		if (!selectedItemModel) {
			return null;
		}

		if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.COMPONENT) {
			const _selectedItemModel = selectedItemModel as ISchemaComponentModel;
			const _flowNodeListModel = _selectedItemModel.events.props[eventName]?.props.nodes.template;

			if (!_flowNodeListModel) {
				return null;
			}

			return {
				kind: EVENT_FLOW_OWNER_KIND.COMPONENT,
				eventName: eventName,
				componentModel: _selectedItemModel,
				flowNodeListModel: _flowNodeListModel
			};
		} else if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.DATASOURCE) {
			const _selectedItemModel = selectedItemModel as ISchemaDataSourceModel;
			const _flowNodeListModel = _selectedItemModel.events.props[eventName]?.props.nodes.template;

			if (!_flowNodeListModel) {
				return null;
			}

			return {
				kind: EVENT_FLOW_OWNER_KIND.DATASOURCE,
				eventName: eventName,
				dataSourceModel: _selectedItemModel,
				flowNodeListModel: _flowNodeListModel
			};
		}
	}

	return null;
}

export const ViewEditorEventFlow_PaletteItem: React.FunctionComponent<IViewEditorEventFlow_PaletteItemProps> =
	({ typeName, nodeType }) => {
		const t = useTranslate();
		const elRef = useRef<HTMLButtonElement>();

		const handleDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>) => {
			event.dataTransfer.setData(
				FLOW_EDITOR_DND_MIMETYPE,
				JSON.stringify({
					id: uuid_v4(),
					varName: null,
					type: typeName,
					opts: {},
					outputs: {},
					position: {
						x: 0,
						y: 0
					}
				})
			);
			event.dataTransfer.effectAllowed = "move";
		}, []);

		const baseClass = BEM("edt-view-editor-event-flow");

		return (
			<button
				className={baseClass.element("palette-item").toClassName()}
				draggable={true}
				onDragStart={handleDragStart}
				ref={elRef}
			>
				<Icon size={ICON_SIZE.CUSTOM} name={nodeType.icon} />
				<span className={baseClass.element("palette-item-label").toClassName()}>
					{t("editor", nodeType.label)}
				</span>
				{nodeType.description ? (
					<Tooltip for={elRef} openDelay={1200}>
						{t("editor", nodeType.description)}
					</Tooltip>
				) : null}
			</button>
		);
	};

export const ViewEditorEventFlow_Palette: React.FunctionComponent<IViewEditorEventFlow_PaletteProps> = () => {
	const baseClass = BEM("edt-view-editor-event-flow");

	// @todo Sorting

	return (
		<nav className={baseClass.element("palette").toClassName()}>
			<div className={baseClass.element("palette-container").toClassName()}>
				{Object.keys(CoreComponentEventNodeTypes).map((k) => {
					const nodeType = CoreComponentEventNodeTypes[
						k
					] as ISchemaFlowNodeTypeDefinition<TGenericBlueprintSchema>;

					if (nodeType.editorOptions.displayInPalette) {
						return <ViewEditorEventFlow_PaletteItem key={k} typeName={k} nodeType={nodeType} />;
					} else {
						return null;
					}
				})}
			</div>
		</nav>
	);
};

export const ViewEditorEventFlowToolbar: React.FunctionComponent<IViewEditorEventFlowToolbarProps> = ({
	view
}) => {
	const t = useTranslate();

	const model = view.getViewModel();
	const editSelection = useMemo(() => model.getSelection(), [ model ]);

	const [ selection, setSelection ] = useState(() => getEventFlowSelection(model, editSelection));
	const [ availableEvents, setAvailableEvents ] = useState<IAvailableEvent[]>(
		() => getAvailableEvents(model, editSelection, t)
	);

	const handleClose = useCallback(() => {
		editSelection.deselectEvent(null);
		view.closeEventFlow();
	}, [ editSelection, view ]);

	const handleZoomIn = useCallback(() => {
		emitEvent(view.__onFlowEditorZoomIn);
	}, [ view ]);

	const handleZoomOut = useCallback(() => {
		emitEvent(view.__onFlowEditorZoomOut);
	}, [ view ]);

	const handleZoomReset = useCallback(() => {
		emitEvent(view.__onFlowEditorZoomReset);
	}, [ view ]);

	const handleFitToView = useCallback(() => {
		emitEvent(view.__onFlowEditorZoomFit);
	}, [ view ]);

	const handleResetLastScope = useCallback(() => {
		if (!selection?.flowNodeListModel) {
			return;
		}

		selection.flowNodeListModel.items.forEach((item) => {
			item.schema.clearExecutionResult(item, true);
		});

		// Emit event to trigger data explorer refresh
		emitEvent(editSelection.onEventNodesChange);
		emitEvent(selection.flowNodeListModel.changeEvent);
	}, [ selection?.flowNodeListModel ]);

	const handleEventMenuItemClick = useCallback((item: IDropdownMenuItem) => {
		const selectedItems = editSelection.getItems();

		if (selectedItems.length === 0) {
			// View-level event
			const specModel = model.getModel().props.spec.value;
			editSelection.clear(false);
			editSelection.selectEvent(specModel, item.id);
		} else if (selectedItems.length === 1) {
			const selectedItemModel = model.getDesignContext().getNodeById(selectedItems[0].nodeId);
			if (selectedItemModel) {
				editSelection.selectEvent(selectedItemModel, item.id);
			}
		}
	}, [ model, editSelection ]);

	useEffect(() => {
		const handleSelectionChange = () => {
			setSelection(getEventFlowSelection(model, editSelection));
			setAvailableEvents(getAvailableEvents(model, editSelection, t));
		};

		const handleEventChange = () => {
			if (editSelection.getEventName()) {
				// Update only when event name is set, otherwise it will cause "blink" when closing...
				setSelection(getEventFlowSelection(model, editSelection));
			}
			setAvailableEvents(getAvailableEvents(model, editSelection, t));
		};

		onEvent(editSelection.onChange, handleSelectionChange);
		onEvent(editSelection.onEventChange, handleEventChange);

		return () => {
			offEvent(editSelection.onChange, handleSelectionChange);
			offEvent(editSelection.onEventChange, handleEventChange);
		};
	}, [ model, editSelection ]);

	useEffect(() => {
		const handleModelChange = () => {
			// Check if selected item still exists
			if (selection && !model.getDesignContext().getNodeById(selection.flowNodeListModel.nodeId)) {
				setSelection(null);
			}
		};

		const handleRender = () => {
			// Simulate change event on flow node list model to force flow editor update on render
			if (selection?.flowNodeListModel) {
				emitEvent(selection.flowNodeListModel.changeEvent);
			}
		};

		onEvent(model.onModelChange, handleModelChange);
		onEvent(model.onRender, handleRender);

		return () => {
			offEvent(model.onModelChange, handleModelChange);
			offEvent(model.onRender, handleRender);
		};
	}, [ editSelection, selection ]);

	const ownerLabel = useMemo(() => {
		if (selection && selection.kind === EVENT_FLOW_OWNER_KIND.COMPONENT) {
			const cmpHandler = selection.componentModel.componentHandler;
			return t("editor", cmpHandler.label) + ` (${selection.componentModel.id.value})`;
		} else if (selection && selection.kind === EVENT_FLOW_OWNER_KIND.DATASOURCE) {
			const dsHandler = selection.dataSourceModel.dsHandler;
			return t("editor", dsHandler.label) + ` (${selection.dataSourceModel.id.value})`;
		} else if (selection && selection.kind === EVENT_FLOW_OWNER_KIND.VIEW) {
			return null;
		}
		return null;
	}, [ selection?.flowNodeListModel ]);

	const eventMenuItems = useMemo<IDropdownMenuItem[]>(() => {
		return availableEvents.map((ev) => ({
			id: ev.name,
			label: ev.label,
			icon: ev.icon || "mdi/lightning-bolt"
		}));
	}, [ availableEvents ]);

	const selectedEvent = useMemo(() => {
		if (!selection) {
			return null;
		}
		return availableEvents.find((e) => e.name === selection.eventName) || null;
	}, [ selection, availableEvents ]);

	const selectedEventLabel = selectedEvent?.label || selection?.eventName || "No event selected";
	const selectedEventIcon = selectedEvent?.icon || "mdi/lightning-bolt";

	// @todo Translate
	return (
		<>
			{ownerLabel ? (
				<>
					<ToolbarNote>{ownerLabel}</ToolbarNote>
					<Icon name="mdi/menu-right" size={ICON_SIZE.SMALL} />
				</>
			) : null}
			<ToolbarButton
				icon={selectedEventIcon}
				label={selectedEventLabel}
				menuItems={eventMenuItems}
				selectedMenuItemId={selection?.eventName}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				menuAnchor={FC_ANCHOR.LEFT_BOTTOM}
				onMenuItemClick={handleEventMenuItemClick}
			/>
			<ToolbarSpacer grow={1} />
			<ToolbarButton icon="mdi/magnify" tooltip="Reset Zoom" onClick={handleZoomReset} />
			<ToolbarButton icon="mdi/minus-circle-outline" tooltip="Zoom Out" onClick={handleZoomOut} />
			<ToolbarButton icon="mdi/plus-circle-outline" tooltip="Zoom In" onClick={handleZoomIn} />
			<ToolbarButton icon="mdi/magnify-scan" tooltip="Fit to View" onClick={handleFitToView} />
			<ToolbarSpacer />
			<ToolbarButton
				icon="mdi/backup-restore"
				tooltip="Reset Node State Data"
				onClick={handleResetLastScope}
			/>
			<ToolbarSpacer />
			<ToolbarButton label="Close" icon="mdi/close" onClick={handleClose} />
		</>
	);
};

export const ViewEditorEventFlowContent: React.FunctionComponent<IViewEditorEventFlowToolbarProps> = ({
	view
}) => {
	const model = view.getViewModel();
	const editSelection = useMemo(() => model.getSelection(), [ model ]);

	const [ selection, setSelection ] = useState(() => getEventFlowSelection(model, editSelection));

	const [ reactFlowInstance, setReactFlowInstance ] = useState<ReactFlowInstance>(null);

	const handleFlowSelectionChange = useCallback(
		(nodes: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>>) => {
			editSelection.setEventNodes(selection?.flowNodeListModel, nodes);
		},
		[ editSelection, selection ]
	);

	const handleReactFlowLoad = useCallback<IFlowEditor["onReactFlowLoad"]>((_reactFlowInstance) => {
		setReactFlowInstance(_reactFlowInstance);
	}, []);

	const canFlowRemoveElements = useCallback(() => {
		return view.isViewActive() && view.isLayoutPanelActive([ "eventFlow" ]);
	}, [ view ]);

	useEffect(() => {
		const handleSelectionChange = () => {
			// setAreMultipleItemsSelected(editSelection.getItems().length > 1);
			setSelection(getEventFlowSelection(model, editSelection));
		};

		const handleEventChange = () => {
			if (editSelection.getEventName()) {
				// Update only when event name is set, othwerise it will cause "blink" when closing...
				setSelection(getEventFlowSelection(model, editSelection));
			}
		};

		onEvent(editSelection.onChange, handleSelectionChange);
		onEvent(editSelection.onEventChange, handleEventChange);

		return () => {
			offEvent(editSelection.onChange, handleSelectionChange);
			offEvent(editSelection.onEventChange, handleEventChange);
		};
	}, [ model, editSelection ]);

	useEffect(() => {
		const handleModelChange = () => {
			// Check if selected item still exists
			if (selection && !model.getDesignContext().getNodeById(selection.flowNodeListModel.nodeId)) {
				setSelection(null);
			}
		};

		const handleRender = () => {
			// Simulate change event on flow node list model to force flow editor update on render
			if (selection?.flowNodeListModel) {
				emitEvent(selection.flowNodeListModel.changeEvent);
			}
		};

		onEvent(model.onModelChange, handleModelChange);
		onEvent(model.onRender, handleRender);

		return () => {
			offEvent(model.onModelChange, handleModelChange);
			offEvent(model.onRender, handleRender);
		};
	}, [ editSelection, selection ]);

	const getNodeInfo = useCallback(
		(nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>) => {
			if (nodeModel.type === "action") {
				const actionRefModel = nodeModel.opts.template as unknown as ISchemaActionRefModel;
				const actionId = actionRefModel?.actionId?.constant?.value;
				const resourceDesc = actionId
					? view.getResourceService().getResourceDescriptorById(actionId)
					: undefined;

				return {
					typeLabel: resourceDesc?.name ?? undefined
				};
			}

			return {};
		},
		[ view ]
	);

	useSimpleEvent(view.__onFlowEditorZoomIn, () => reactFlowInstance.zoomIn(), [ view ], true);

	useSimpleEvent(view.__onFlowEditorZoomOut, () => reactFlowInstance.zoomOut(), [ view ], true);

	useSimpleEvent(view.__onFlowEditorZoomReset, () => reactFlowInstance.zoomTo(1.0), [ view ], true);

	useSimpleEvent(
		view.__onFlowEditorZoomFit,
		() =>
			reactFlowInstance.fitView({
				maxZoom: 1.0
			}),
		[ view ],
		true
	);

	const baseClass = BEM("edt-view-editor-event-flow");

	return (
		<Panel scroll={false} className={baseClass.element("content").toClassName()}>
			{selection ? (
				<>
					<ViewEditorEventFlow_Palette />
					<FlowEditor
						modelNode={selection.flowNodeListModel}
						layoutType={FLOW_EDITOR_LAYOUT.VERTICAL}
						className={baseClass.element("flow-editor").toClassName()}
						onSelectionChange={handleFlowSelectionChange}
						onReactFlowLoad={handleReactFlowLoad}
						canRemoveElements={canFlowRemoveElements}
						getNodeInfo={getNodeInfo as TGetNodeInfoFn<ISchemaFlowNodeTypeDefinitionMap>}
					/>
				</>
			) : (
				// @todo Translate
				<div className={baseClass.element("no-event").toClassName()}>No event is selected.</div>
			)}
		</Panel>
	);
};
